import { usePagination, useActions, Page } from "@nosto/search-js/preact/hooks"
import Icon from "@/elements/Icon/Icon"
import { JSX } from "preact/jsx-runtime"
import style from "./Pagination.module.css"
import { getPageUrl } from "@/mapping/url/getPageUrl"
import { cl } from "@nosto/search-js/utils"

type Props = {
  ariaLabel?: string
} & JSX.IntrinsicElements["a"]

function PageLink({ onClick, href, className, ariaLabel, children }: Props) {
  return (
    <a className={cl(style.link, className)} href={href} aria-label={ariaLabel} onClick={onClick}>
      {children}
    </a>
  )
}

export default function Pagination() {
  const { prev, first, pages, last, next } = usePagination({
    width: 5
  })

  const { updateSearch } = useActions()

  function pageLinkProps({ from, page }: Page) {
    return {
      href: getPageUrl(page),
      onClick: (e: Event) => {
        e.preventDefault()
        updateSearch({ products: { from } })
        scrollTo(0, 0)
      }
    }
  }

  // Build the items as one flat list where every <li> has a stable key. Mixing keyed (`pages`)
  // and unkeyed siblings (prev/first/ellipses/last/next) made Preact misplace the page nodes when
  // the surrounding first/last/ellipsis items appeared on navigation — pages got appended to the
  // end of the row (e.g. `< 1 ... ... 15 > 8 9 10 11`). Consistent keys keep reconciliation correct.
  const items: JSX.Element[] = []

  if (prev) {
    items.push(
      <li key="prev">
        <PageLink {...pageLinkProps(prev)} ariaLabel="Previous page">
          <Icon name="arrow-left" />
        </PageLink>
      </li>
    )
  }

  if (first) {
    items.push(
      <li key="first">
        <PageLink {...pageLinkProps(first)} ariaLabel="First page">
          {first.page}
        </PageLink>
      </li>,
      <li key="filler-start">
        <span className={style.filler}>...</span>
      </li>
    )
  }

  pages.forEach(page => {
    items.push(
      <li key={`page-${page.page}`} className={cl(page.current && style.active)}>
        <PageLink ariaLabel={`${page.page} page`} {...pageLinkProps(page)}>
          {page.page}
        </PageLink>
      </li>
    )
  })

  if (last) {
    items.push(
      <li key="filler-end">
        <span className={style.filler}>...</span>
      </li>,
      <li key="last">
        <PageLink {...pageLinkProps(last)} ariaLabel="Last page">
          {last.page}
        </PageLink>
      </li>
    )
  }

  if (next) {
    items.push(
      <li key="next">
        <PageLink {...pageLinkProps(next)} ariaLabel="Next page">
          <Icon name="arrow-right" />
        </PageLink>
      </li>
    )
  }

  return <ul className={style.container}>{items}</ul>
}
